<template>
  <div v-if="!published" class="form-card" :class="{'form-card--loading': lock}">
    <div class="form-card__header">
      <span class="form-card__tag">{{ $t('tag') }}</span>
      <h2 class="form-card__title">
        {{ $t('title') }}
      </h2>
    </div>

    <div class="form-card__body">
      <CameraSelector v-if="cameras.length && !waitingPermission" @selected="changeVideo" />

      <template v-if="streamError">
        <VideoRoomMediaErrors />
      </template>

      <div v-show="!streamError" class="preview-wrap">
        <div class="preview-area">
          <video
            v-show="cameras && cameras.length && video && video.srcObject"
            ref="video"
            class="preview-video"
            autoplay
            muted="muted"
            playsinline
          />
          <div v-if="!(video && video.srcObject)" class="preview-placeholder">
            <template v-if="waitingPermission">
              <div class="preview-spinner">
                <div /><div /><div /><div />
              </div>
              <p class="preview-placeholder__text">
                {{ $t('requestingCamera') }}
              </p>
            </template>
            <template v-else-if="cameras.length">
              <div class="preview-spinner">
                <div /><div /><div /><div />
              </div>
            </template>
            <template v-else>
              <img src="../assets/img/icons/no-camera.svg" width="40" height="40" alt="" />
              <p class="preview-placeholder__text">
                {{ $t('noCamera') }}
              </p>
            </template>
          </div>
        </div>

        <div
          class="form-controls"
          :class="{'form-controls--no-camera': !cameras.length || waitingPermission}"
        >
          <input
            v-model="publisherName"
            :class="{error}"
            :placeholder="$t('namePlaceholder')"
            class="name-input"
          >
          <div class="btn-row">
            <button class="btn btn--secondary" :disabled="lock" @click="loginWithoutVideo">
              {{ $t('enterWithoutVideo') }}
            </button>
            <button
              v-if="cameras && cameras.length && video && video.srcObject"
              class="btn btn--primary"
              :disabled="lock"
              @click="login"
            >
              {{ $t('enterWithVideo') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="lock" class="form-card__overlay">
      <div class="form-card__overlay-spinner">
        <div /><div /><div /><div />
      </div>
      <p class="form-card__overlay-text">
        {{ $t('connecting') }}
      </p>
    </div>

    <div class="form-card__footer">
      <div class="mic-row">
        <img src="../assets/img/icons/microphone-label.svg" class="mic-row__icon" width="16" height="16" alt="" />
        <MicrophoneSelector @selected="changeAudio">
          <MicrophoneLevel :level="audioValue" />
        </MicrophoneSelector>
      </div>
    </div>
  </div>
</template>

<script>
import useJanusPublisher from '@/compositions/useJanusPublisher';
import {
  computed, nextTick, onMounted, ref, watch,
} from '@vue/composition-api';
import useRoom from '@/compositions/useRoom';
import { UserType } from '@/enums';
import useStreams from '@/compositions/useStreams';
import useVideo from '@/compositions/useVideo';
import useCamera from '@/compositions/useCamera';
import VideoRoomMediaErrors from '@/components/VideoRoomMediaErrors.vue';
import CameraSelector from '@/components/CameraSelector.vue';
import useMicrophone from '@/compositions/useMicrophone';
import MicrophoneSelector from '@/components/MicrophoneSelector.vue';
import MicrophoneLevel from '@/components/MicrophoneLevel.vue';
import useJanusSession from '@/compositions/useJanusSession';
import { pushDebugLog } from '@/helpers';

export default {
  name: 'VideoRoomEnterForm',
  components: {
    MicrophoneLevel,
    MicrophoneSelector,
    CameraSelector,
    VideoRoomMediaErrors,
  },
  setup(_, context) {
    const {
      root: {
        $router,
        $route,
      },
    } = context;

    const loaded = ref(false);
    const error = ref(false);

    const {
      published,
      name,
      setName,
      startPublish,
      fetchPublishers,
    } = useJanusPublisher();

    const { createSession } = useJanusSession();
    const { error: streamError, waitingPermission } = useStreams();

    const {
      setMicrophone,
      setCamera,
      getLevel,
      requestMediaStream,
      myStream,
    } = useStreams();

    const { getStreamByMicrophoneId } = useMicrophone();
    const {
      setVideo,
      play,
    } = useVideo();
    const video = ref(null);
    const audioValue = ref(null);
    const audioValueInterval = ref(null);

    const videoStream = ref(null);
    const audioStream = ref(null);

    function setLevel(stream) {
      getLevel(stream, (value, interval) => {
        audioValue.value = parseFloat(value);
        audioValueInterval.value = interval;
      });
    }

    function stopVideoStream() {
      // eslint-disable-next-line no-unused-expressions
      videoStream.value?.getTracks()
        .forEach((track) => {
          track.stop();
        });
    }

    function stopAudioStream() {
      // eslint-disable-next-line no-unused-expressions
      audioStream.value?.getTracks()
        .forEach((track) => {
          track.stop();
        });
    }

    const camerasList = ref([]);
    const {
      loadCameras,
      cameras,
    } = useCamera();
    const {
      loadMicrophones,
      microphones,
    } = useMicrophone();

    const { cameraDisabled } = useStreams();

    const { user, existingRoom, fetchRoom } = useRoom();

    const publisherName = computed({
      get() {
        if (user.isAdmin) {
          return name.value || '';
        }
        return name.value
            || `${existingRoom.value?.candidate.firstName} ${existingRoom.value?.candidate.lastName}`;
      },
      set(val) {
        error.value = false;
        name.value = val;
      },
    });

    const lock = ref(false);

    function getEnterDebugPayload(extra = {}) {
      return {
        roomId: existingRoom.value?.roomId,
        published: published.value,
        publisherName: publisherName.value,
        lock: lock.value,
        streamError: streamError.value?.name || streamError.value?.message || null,
        waitingPermission: waitingPermission.value,
        cameraDisabled: cameraDisabled.value,
        camerasCount: cameras.value?.length || 0,
        microphonesCount: microphones.value?.length || 0,
        hasPreviewStream: !!video.value?.srcObject,
        ...extra,
      };
    }

    async function login() {
      if (!publisherName.value || lock.value) {
        error.value = true;
        pushDebugLog('enter', 'login blocked by validation', getEnterDebugPayload());
        return;
      }

      pushDebugLog('enter', 'login started', getEnterDebugPayload());
      lock.value = true;
      stopAudioStream();
      stopVideoStream();
      setName(publisherName.value);
      await createSession();
      await fetchRoom();
      await fetchPublishers();
      await startPublish();
      lock.value = false;
    }

    async function loginWithoutVideo() {
      pushDebugLog('enter', 'loginWithoutVideo clicked', getEnterDebugPayload());
      cameraDisabled.value = true;
      await login();
    }

    async function loadMedia(type) {
      pushDebugLog('enter', 'loadMedia started', getEnterDebugPayload({ type }));
      if (type === 'video' || !type) {
        videoStream.value = await requestMediaStream({ audio: false });
        await setVideo(videoStream, video);
      }
      if (type === 'audio' || !type) {
        audioStream.value = await requestMediaStream({ audio: {}, video: false });
        setLevel(audioStream.value);
      }
      pushDebugLog('enter', 'loadMedia finished', getEnterDebugPayload({
        type,
        hasVideoStream: !!videoStream.value,
        hasAudioStream: !!audioStream.value,
      }));
    }

    function changeVideo(deviceId) {
      pushDebugLog('enter', 'changeVideo', getEnterDebugPayload({ deviceId }));
      stopVideoStream();
      setCamera(deviceId);
      loadMedia('video');
    }

    function changeAudio(deviceId) {
      pushDebugLog('enter', 'changeAudio', getEnterDebugPayload({ deviceId }));
      stopAudioStream();
      clearInterval(audioValueInterval.value);
      setMicrophone(deviceId);
      loadMedia('audio');
    }

    async function load() {
      pushDebugLog('enter', 'load started');
      await loadCameras();
      await loadMicrophones();
      setCamera(cameras.value[0]?.deviceId);
      setMicrophone(microphones.value[0]?.deviceId);
      pushDebugLog('enter', 'devices loaded', getEnterDebugPayload({
        initialCameraId: cameras.value[0]?.deviceId,
        initialMicrophoneId: microphones.value[0]?.deviceId,
      }));
      loadMedia();
    }

    useCamera(() => {
      nextTick(() => {
        if (!cameras.value.length) return;
        camerasList.value = cameras.value;
        changeVideo(cameras.value[0]?.deviceId);
      });
    });

    useMicrophone(() => {
      nextTick(() => {
        if (!microphones.value.length) return;
        changeAudio(microphones.value[0]?.deviceId);
      });
    });

    const hasMicrophonePermission = computed(
      () => microphones.value.length > 0 && microphones.value[0].deviceId,
    );

    onMounted(async () => {
      await load();
    });

    return {
      user,
      login,
      loginWithoutVideo,
      published,
      name,
      setName,
      publisherName,
      UserType,
      video,
      stream: videoStream,
      changeVideo,
      changeAudio,
      audioValue,
      load,
      cameras,
      error,
      lock,
      streamError,
      microphones,
      hasMicrophonePermission,
      waitingPermission,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-card {
  width: 100%;
  max-width: 45rem;
  background: #FFFFFF;
  border-radius: 1rem;
  box-shadow: 0 4px 24px rgba(10, 130, 217, 0.08), 0 1px 4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  text-align: left;
  position: relative;

  &__overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.88);
    backdrop-filter: blur(4px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    z-index: 10;
    border-radius: 1rem;
  }

  &__overlay-text {
    font-size: 0.9rem;
    color: #6B8BAF;
    margin: 0;
  }

  &__overlay-spinner {
    display: inline-block;
    position: relative;
    width: 48px;
    height: 48px;

    div {
      box-sizing: border-box;
      display: block;
      position: absolute;
      width: 38px;
      height: 38px;
      margin: 5px;
      border: 3px solid transparent;
      border-radius: 50%;
      animation: overlay-spin 1.1s cubic-bezier(0.5, 0, 0.5, 1) infinite;

      &:nth-child(1) { animation-delay: -0.45s; border-top-color: #0A82D9; }
      &:nth-child(2) { animation-delay: -0.3s;  border-top-color: #1aA8E8; }
      &:nth-child(3) { animation-delay: -0.15s; border-top-color: #00CBF3; }
      &:nth-child(4) { border-top-color: #60DDF8; }
    }
  }

  &__header {
    padding: 1.75rem 2.5rem 1.5rem;
    border-bottom: 1px solid #F0F4F8;
    background: linear-gradient(135deg, #F8FBFF 0%, #FFFFFF 100%);
  }

  &__tag {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #0A82D9;
    background: rgba(10, 130, 217, 0.08);
    border-radius: 4px;
    padding: 0.2rem 0.6rem;
    margin-bottom: 0.6rem;
  }

  &__title {
    font-size: 1.375rem;
    font-weight: 500;
    line-height: 1.25;
    color: #1A1A2E;
    margin: 0;
  }

  &__body {
    padding: 1.5rem 2.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__footer {
    padding: 1rem 2.5rem 1.75rem;
    border-top: 1px solid #F0F4F8;
  }
}

.preview-wrap {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.preview-area {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #1C1C1E;
  border-radius: 0.75rem;
  overflow: hidden;
}

.preview-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(-1, 1);
  display: block;
}

.preview-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.875rem;
  color: rgba(255, 255, 255, 0.25);

  &__text {
    font-size: 0.8125rem;
    color: rgba(255, 255, 255, 0.4);
    margin: 0;
    text-align: center;
  }
}

.preview-spinner {
  display: inline-block;
  position: relative;
  width: 44px;
  height: 44px;

  div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 34px;
    height: 34px;
    margin: 5px;
    border: 3px solid transparent;
    border-radius: 50%;
    animation: preview-spin 1.1s cubic-bezier(0.5, 0, 0.5, 1) infinite;

    &:nth-child(1) { animation-delay: -0.45s; border-top-color: rgba(255,255,255,0.7); }
    &:nth-child(2) { animation-delay: -0.3s;  border-top-color: rgba(255,255,255,0.5); }
    &:nth-child(3) { animation-delay: -0.15s; border-top-color: rgba(255,255,255,0.3); }
    &:nth-child(4) { border-top-color: rgba(255,255,255,0.15); }
  }
}

@keyframes preview-spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes overlay-spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.form-controls {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;

  &--no-camera {
    padding: 0;
  }
}

.name-input {
  width: 100%;
  background: #F5F7FA;
  border: 1.5px solid #E8EDF3;
  border-radius: 8px;
  padding: 0.7rem 1rem;
  font-size: 0.9375rem;
  font-family: inherit;
  color: #1A1A2E;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: #9BAEC4;
  }

  &:focus {
    border-color: #0A82D9;
    box-shadow: 0 0 0 3px rgba(10, 130, 217, 0.1);
    outline: none;
    background: #FFFFFF;
  }

  &.error {
    border-color: #E53935;
    box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1);
  }
}

.btn-row {
  display: flex;
  gap: 0.75rem;
}

.btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: inherit;
  border: none;
  border-radius: 8px;
  padding: 0.7rem 1rem;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.15s ease, opacity 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  &:focus {
    outline: none;
  }

  &--secondary {
    color: #0A82D9;
    background: rgba(10, 130, 217, 0.08);

    &:not(:disabled):hover {
      background: rgba(10, 130, 217, 0.14);
    }
  }

  &--primary {
    color: #FFFFFF;
    background: linear-gradient(135deg, #0A82D9 0%, #00CBF3 100%);
    box-shadow: 0 3px 10px rgba(10, 130, 217, 0.35);

    &:not(:disabled):hover {
      box-shadow: 0 5px 16px rgba(10, 130, 217, 0.45);
      transform: translateY(-1px);
    }

    &:not(:disabled):active {
      transform: translateY(0);
      box-shadow: 0 2px 6px rgba(10, 130, 217, 0.3);
    }
  }
}

.mic-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &__icon {
    flex-shrink: 0;
  }

  :deep(.microphone-selector) {
    flex: 1;
  }
}

@media (max-width: 640px) {
  .form-card {
    &__header {
      padding: 1.5rem 1.5rem 1.25rem;
    }

    &__body {
      padding: 1.25rem 1.5rem;
    }

    &__footer {
      padding: 0.875rem 1.5rem 1.5rem;
    }
  }

  .btn-row {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}
</style>

<i18n locale="en" lang="yaml">
tag: Video interview
title: Camera setup
namePlaceholder: Enter your name
enterWithVideo: Enter with video
enterWithoutVideo: Enter without video
requestingCamera: Requesting camera access...
noCamera: No camera found
connecting: Connecting...
</i18n>

<i18n locale="ru" lang="yaml">
tag: Видеособеседование
title: Настройка камеры
namePlaceholder: Введите ваше имя
enterWithVideo: Войти с видео
enterWithoutVideo: Войти без видео
requestingCamera: Запрашиваем доступ к камере...
noCamera: Камера не обнаружена
connecting: Подключение...
</i18n>
